import type { ReactNode } from 'react';
import { Suspense, useEffect } from 'react';
import {
	BrowserRouter,
	Outlet,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from 'react-router-dom';
import AnnouncementPopups from '../components/AnnouncementPopups';
import AppLocationGuard from '../components/AppLocationGuard';
import EurlNavigationGuard from '../components/EurlNavigationGuard';
import V2ProgressReporter from '../components/V2ProgressReporter';
import { LeiLookupProvider } from '../contexts/LeiLookupContext';
import {
	PageProgressProvider,
	RouteSuspenseFallback,
	RouteSuspenseTracker,
} from '../contexts/PageProgressContext';
import AccessError from '../pages/AccessError';
import DownloadSuccess from '../pages/DownloadSuccess';
import { Skeleton as EditorialPageSkeleton } from '../pages/EditorialPage/Skeleton';
import GridPageSkeleton from '../pages/Grid/components/GridPageSkeleton';
import ComparePageSkeleton from '../pages/OpinionCompare/component/skelton';
import PDFViewerPage from '../pages/PDFViewerPage';
import { PrivateBankingPageSkeleton } from '../pages/PrivateBanking/components/PrivateBankingSkeleton';
import PublicAccessError from '../pages/PublicAccessError';
import ReportPageSkeleton from '../pages/ReportSingle/components/ReportPageSkeleton';
import { SpeedPageFallback } from '../pages/SpeedPage/components/SpeedPageSkeleton';
import VersionHistorySkeleton from '../pages/version-history/components/VersionHistorySkeleton';
import { applyTheme } from '../theme/applyTheme';
import {
	initSingleThemeModeFromUrl,
	isSingleThemeMode,
	setSingleThemeMode,
	SINGLE_THEME_ID,
} from '../theme/singleTheme';
import { setGlobalNavigate } from '../util/globalNavigate';
import { lazyWithRetry } from '../util/lazyWithRetry';
import IntermediateAuthRoute from './IntermediateAuthRoute';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import { ROUTE_PATHS } from './routePaths';

/** Nested Suspense so report routes show a page skeleton instead of the global dark loader. */
const withReportSkeleton = (element: ReactNode, fallback: ReactNode = <ReportPageSkeleton />) => (
	<Suspense fallback={<RouteSuspenseFallback>{fallback}</RouteSuspenseFallback>}>{element}</Suspense>
);

/** /report-view-link?type=version redirects to Version History, so show that skeleton instead of the report one. */
const ReportViewLinkFallback = () => {
	const { search } = useLocation();
	const isVersionHistory = new URLSearchParams(search).get('type') === 'version';
	return isVersionHistory ? <VersionHistorySkeleton /> : <ReportPageSkeleton />;
};

const FALLBACK_PARENT_ORIGIN = process.env.REACT_APP_V3_SHELL_ORIGIN;

function getParentOrigin() {
	// Referrer becomes the previous V2 page after a full navigation inside the
	// iframe. The parent frame origin does not.
	const ancestorOrigin = globalThis.location.ancestorOrigins?.item(0);
	if (ancestorOrigin) return ancestorOrigin;
	return FALLBACK_PARENT_ORIGIN || undefined;
}

// Buffer THEME_CHANGED messages that arrive before React mounts so they aren't lost.
let _bufferedThemeId: number | null = null;
function _earlyMessageHandler(event: MessageEvent) {
	// Checked here too, not just in the mounted handler: this one persists the
	// single-theme lock, so it must not act on a message from any other origin.
	const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
	if (!parentOrigin || event.origin !== parentOrigin) return;

	const data = event.data as
		| { type?: string; themeId?: number; singleTheme?: boolean }
		| undefined;
	if (data?.type !== 'THEME_CHANGED') return;
	if (typeof data.singleTheme === 'boolean') {
		setSingleThemeMode(data.singleTheme);
	}
	if (data.singleTheme === true) {
		_bufferedThemeId = SINGLE_THEME_ID;
		return;
	}
	if (typeof data.themeId === 'number') {
		_bufferedThemeId = data.themeId;
	}
}
window.addEventListener('message', _earlyMessageHandler);

// Seeded from `?__v3singleTheme=1` on the iframe src so the first paint is
// already neutral, before any shell message or categories API response lands.
initSingleThemeModeFromUrl();

function LeiRoutesProvider() {
	return (
		<LeiLookupProvider>
			<Outlet />
		</LeiLookupProvider>
	);
}

// Prefetched: downloaded automatically after main bundle finishes loading
const TermsAndConditions = lazyWithRetry(
	() => import(/* webpackPrefetch: true */ '../pages/authPages/TermsAndCondition'),
	'terms-and-conditions'
);
const CategoryHomePage = lazyWithRetry(
	() => import(/* webpackPrefetch: true */ '../pages/Home'),
	'category-home-page'
);

// Lazy-loaded: downloaded on-demand when navigated to
const AddAlert = lazyWithRetry(
	() => import('../pages/AddAlerts'),
	'add-alert'
);
const AddInternalNote = lazyWithRetry(
	() => import('../pages/AddInternalNote'),
	'add-internal-note'
);
const AddNewReport = lazyWithRetry(
	() => import('../pages/AddNewReport'),
	'add-new-report'
);
const AdvancedSearch = lazyWithRetry(
	() => import('../pages/AdvancedSearch'),
	'advanced-search'
);
const AllAlerts = lazyWithRetry(
	() => import('../pages/AllAlerts'),
	'all-alerts'
);
const Forgotpassword = lazyWithRetry(
	() => import('../pages/authPages/ForgotPassword'),
	'forgotpassword'
);
const Login = lazyWithRetry(
	() => import('../pages/authPages/Login'),
	'login'
);
const OtpVerification = lazyWithRetry(
	() => import('../pages/authPages/OtpVerification'),
	'otp-verification'
);
const ResetPassword = lazyWithRetry(
	() => import('../pages/authPages/ResetPassword'),
	'reset-password'
);
const ChangePassword = lazyWithRetry(
	() => import('../pages/ChangePassword'),
	'change-password'
);
const CKEditor = lazyWithRetry(
	() => import('../pages/CKEditor'),
	'ck-editor'
);
const AlertDocumentLinkPlugin = lazyWithRetry(
	() => import('../pages/CKEditor/AlertDocumentLinkPlugin'),
	'alert-document-link-plugin'
);
const CkEditorImageBrowser = lazyWithRetry(
	() => import('../pages/CKEditor/CkEditorImageBrowser'),
	'ck-editor-image-browser'
);
const DefinationPlugin = lazyWithRetry(
	() => import('../pages/CKEditor/DefinationPlugin'),
	'defination-plugin'
);
const DocumentDefinationAlertPlugin = lazyWithRetry(
	() => import('../pages/CKEditor/DocumentDefinationAlertPlugin'),
	'document-defination-alert-plugin'
);
const DocumentLinkPlugin = lazyWithRetry(
	() => import('../pages/CKEditor/DocumentLinkPlugin'),
	'document-link-plugin'
);
const GridDefinationPlugin = lazyWithRetry(
	() => import('../pages/CKEditor/GridDefinationPlugin'),
	'grid-defination-plugin'
);
const GridDocumentLinkPlugin = lazyWithRetry(
	() => import('../pages/CKEditor/GridDocumentPlugin'),
	'grid-document-link-plugin'
);
const PageDocumentLinkPlugin = lazyWithRetry(
	() => import('../pages/CKEditor/PageDocumentLinkPlugin'),
	'page-document-link-plugin'
);
const ReportDefinationPlugin = lazyWithRetry(
	() => import('../pages/CKEditor/ReportDefinationPlugin'),
	'report-defination-plugin'
);
const ViewDocument = lazyWithRetry(
	() => import('../pages/CKEditor/ViewDocument'),
	'view-document'
);
const DownloadResource = lazyWithRetry(
	() => import('../pages/DownloadResource'),
	'download-resource'
);
const EditorialPage = lazyWithRetry(
	() => import('../pages/EditorialPage'),
	'editorial-page'
);
const EditPage = lazyWithRetry(
	() => import('../pages/EditPage'),
	'edit-page'
);
const EditReportContainer = lazyWithRetry(
	() => import('../pages/EditReport'),
	'edit-report'
);
const FindGrid = lazyWithRetry(
	() => import('../pages/FindGrid'),
	'find-grid'
);
const GridViewLink = lazyWithRetry(
	() => import('../pages/FindGrid/GridViewLink'),
	'grid-view-link'
);
const FindPage = lazyWithRetry(
	() => import('../pages/FindPage'),
	'find-page'
);
const PageViewLink = lazyWithRetry(
	() => import('../pages/FindPage/PageViewLink'),
	'page-view-link'
);
const FindReport = lazyWithRetry(
	() => import('../pages/FindReport'),
	'find-report'
);
const ReportViewLink = lazyWithRetry(
	() => import('../pages/FindReport/ReportViewLink'),
	'report-view-link'
);
const GridSettings = lazyWithRetry(
	() => import('../pages/Grid/components/GridSettings'),
	'grid-settings'
);
const ContentTileViewGridPage = lazyWithRetry(
	() => import('../pages/Grid/ContentTileViewGrid'),
	'content-tile-view-grid-page'
);
const EditGrid = lazyWithRetry(
	() => import('../pages/Grid/EditGrid'),
	'edit-grid'
);
const GridView = lazyWithRetry(
	() => import('../pages/Grid/GridView'),
	'grid-view'
);
const GridViewFilter = lazyWithRetry(
	() => import('../pages/Grid/GridViewFilter'),
	'grid-view-filter'
);
const ViewGrid = lazyWithRetry(
	() => import('../pages/Grid/ViewGrid'),
	'view-grid'
);
const GuestUserReports = lazyWithRetry(
	() => import('../pages/GuestUserReports'),
	'guest-user-reports'
);
const UnknownPathRoute = lazyWithRetry(
	() => import('./UnknownPathRoute'),
	'unknown-path-route'
);
const MyAlert = lazyWithRetry(
	() => import('../pages/MyAlert'),
	'my-alert'
);
const MyReviews = lazyWithRetry(
	() => import('../pages/MyReviews'),
	'my-reviews'
);
const OpinionCompare = lazyWithRetry(
	() => import('../pages/OpinionCompare'),
	'opinion-compare'
);
const OpinionCompareTab = lazyWithRetry(
	() => import('../pages/OpinionCompareTab'),
	'opinion-compare-tab'
);
const PageVersionComparison = lazyWithRetry(
	() => import('../pages/PageVersionComparison'),
	'page-version-comparison'
);
const PDFPageDownload = lazyWithRetry(
	() => import('../pages/PDFPageDownload'),
	'pdf-page-download'
);
const AlertDocumentDownload = lazyWithRetry(
	() => import('../pages/AlertDocDownload'),
	'alert-document-download'
);
const ConditionOfUseDocumentDownload = lazyWithRetry(
	() => import('../pages/ConditionOfUseDocDownload'),
	'condition-of-use-document-download'
);
const PrivateBankingDocumentDownload = lazyWithRetry(
	() => import('../pages/PrivateBankingDocDownload'),
	'private-banking-document-download'
);
const PrivateBankingReport = lazyWithRetry(
	() => import('../pages/PrivateBanking/PrivateBankingReport'),
	'private-banking-report'
);
const Product = lazyWithRetry(
	() => import('../pages/Product'),
	'product'
);
const ProductRedirect = lazyWithRetry(
	() => import('../pages/ProductRedirect'),
	'product-redirect'
);
const ReportChangeOnly = lazyWithRetry(
	() => import('../pages/ReportChangesOnly'),
	'report-change-only'
);
const ReportCompare = lazyWithRetry(
	() => import('../pages/ReportCompare'),
	'report-compare'
);
const Reporting = lazyWithRetry(
	() => import('../pages/Reporting'),
	'reporting'
);
const ReportMultiContainer = lazyWithRetry(
	() => import('../pages/ReportMulti'),
	'report-multi-container'
);
const ReportSingleContainer = lazyWithRetry(
	() => import('../pages/ReportSingle'),
	'report-single-container'
);
const ReportTabContainer = lazyWithRetry(
	() => import('../pages/ReportTab'),
	'report-tab-container'
);
const ReportView = lazyWithRetry(
	() => import('../pages/ReportView'),
	'report-view'
);
const RichTextBoxPopup = lazyWithRetry(
	() => import('../pages/RichTextBoxPopup'),
	'rich-text-box-popup'
);
const SearchDocumentDownload = lazyWithRetry(
	() => import('../pages/SearchDocDownload'),
	'search-document-download'
);
const SpeedPage = lazyWithRetry(
	() => import('../pages/SpeedPage'),
	'speed-page'
);
const StandardError = lazyWithRetry(
	() => import('../pages/StandardError'),
	'standard-error'
);
const TestErrorHandling = lazyWithRetry(
	() => import('../pages/TestErrorHandling'),
	'test-error-handling'
);
const ToastDemo = lazyWithRetry(
	() => import('../pages/ToastDemo'),
	'toast-demo'
);
const GridVersionHistory = lazyWithRetry(
	() => import('../pages/version-history/GridVersionHistory'),
	'grid-version-history'
);
const PageVersionHistory = lazyWithRetry(
	() => import('../pages/version-history/PageVersionHistory'),
	'page-version-history'
);
const ReportVersionHistory = lazyWithRetry(
	() => import('../pages/version-history/ReportversionHistory'),
	'report-version-history'
);
const HandelCFMLinks = lazyWithRetry(
	() => import('../pages/HandelCFMLinks'),
	'handel-cfm-links'
);
const InternalNotesAttachment = lazyWithRetry(
	() => import('../pages/InternalNotesAttachment'),
	'internal-notes-attachment'
);
const SsoError = lazyWithRetry(
	() => import('../pages/authPages/SsoError'),
	'sso-error'
);
const SsoOtpLogin = lazyWithRetry(
	() => import('../pages/authPages/SsoOtpLogin'),
	'sso-otp-login'
);
const SilentSsoCallback = lazyWithRetry(
	() => import('../pages/authPages/SilentSsoCallback'),
	'silent-sso-callback'
);

const LEIHomePage = lazyWithRetry(
	() => import('../pages/LEI/LEI-homepage'),
	'lei-home'
);
const LEINettingAnalysisPage = lazyWithRetry(
	() => import('../pages/LEI/LEI-Netting-Analysis-Page'),
	'lei-netting-analysis'
);
const LEIOutComePage = lazyWithRetry(
	() => import('../pages/LEI/LEI-OutCome-Page'),
	'lei-outcome'
);
const Logout = lazyWithRetry(
	() => import('../pages/Logout'),
	'logout'
);
const PageMoved = lazyWithRetry(
	() => import('../pages/PageMoved'),
	'page-moved'
);
const PublicStandardError = lazyWithRetry(
	() => import('../pages/PublicStandardError'),
	'public-standard-error'
);
const SourceDocumentDownload = lazyWithRetry(
	() => import('../pages/SourceDocDownload'),
	'source-doc-download'
);

const AppRoutesContent = () => {
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		setGlobalNavigate(navigate);
	}, [navigate]);

	// Apply buffered pre-mount THEME_CHANGED message (from shell) or default 101.
	// Listing/report pages resolve product theme from categoryId via categories API —
	// do not force 101 when categoryId is present (avoids a gray flash / race with THEME_CHANGED).
	// Do not overwrite a theme already applied by a child page in useLayoutEffect.
	useEffect(() => {
		window.removeEventListener('message', _earlyMessageHandler);
		if (isSingleThemeMode()) {
			applyTheme(SINGLE_THEME_ID);
			_bufferedThemeId = null;
		} else if (_bufferedThemeId !== null) {
			applyTheme(_bufferedThemeId);
			_bufferedThemeId = null;
		} else if (!document.documentElement.dataset.theme) {
			const hasCategoryId = new URLSearchParams(window.location.search).has(
				'categoryId'
			);
			if (!hasCategoryId) {
				applyTheme(101);
			}
		}
	}, []);

	// Allow the parent shell (V3) to drive navigation and theme inside the iframe.
	useEffect(() => {
		const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
		if (!parentOrigin) return;

		const handler = (event: MessageEvent) => {
			if (event.origin !== parentOrigin) return;

			const data = event.data as {
				type?: string;
				path?: string;
				themeId?: number;
				singleTheme?: boolean;
			} | undefined;
			if (!data || typeof data !== 'object') return;

			if (data.type === 'THEME_CHANGED') {
				// The shell owns single-theme mode; while it is on, stop honouring
				// per-product theme ids (including ones this app resolves itself).
				if (typeof data.singleTheme === 'boolean') {
					setSingleThemeMode(data.singleTheme);
				}
				if (data.singleTheme === true) {
					applyTheme(SINGLE_THEME_ID);
					return;
				}
				if (typeof data.themeId === 'number') {
					applyTheme(data.themeId);
				}
				return;
			}

			if (data.type === 'NAVIGATE' && typeof data.path === 'string') {
				void navigate(data.path);
			}
		};

		window.addEventListener('message', handler);
		return () => window.removeEventListener('message', handler);
	}, [navigate]);

	// Notify parent shell about route changes (sidebar sync / breadcrumbs).
	useEffect(() => {
		if (window.parent === window) return;
		const parentOrigin = getParentOrigin() ?? FALLBACK_PARENT_ORIGIN;
		if (!parentOrigin) return;

		const themeAttribute = document.documentElement.dataset.theme;
		window.parent.postMessage(
			{
				type: 'ROUTE_CHANGED',
				path: location.pathname + location.search,
				// Only report a theme once applied — avoid echoing default 101 before
				// useProductCategoryTheme resolves from categoryId (parent may THEME_CHANGED back).
				...(themeAttribute
					? { currentThemeId: Number.parseInt(themeAttribute, 10) }
					: {}),
			},
			parentOrigin
		);
	}, [location.pathname, location.search]);

	return (
		<PageProgressProvider routeKey={`${location.pathname}${location.search}`}>
			<EurlNavigationGuard />
			<AnnouncementPopups />
			<Suspense fallback={<RouteSuspenseFallback>{null}</RouteSuspenseFallback>}>
			<Routes>
				{/* Public */}
				<Route path={ROUTE_PATHS.RESET_PASSWORD} element={<ResetPassword />} />
				<Route path={ROUTE_PATHS.SSO_ERROR} element={<SsoError />} />
				<Route path={ROUTE_PATHS.SSO_OTP_VERIFICATION} element={<SsoOtpLogin />} />
				<Route
					path={ROUTE_PATHS.SILENT_SSO_CALLBACK}
					element={<SilentSsoCallback />}
				/>
				<Route element={<PublicRoute />}>
					<Route path={ROUTE_PATHS.LOGIN} element={<Login />} />
					<Route
						path={ROUTE_PATHS.OTP_VERIFICATION}
						element={<OtpVerification />}
					/>
					<Route
						path={ROUTE_PATHS.FORGOT_PASSWORD}
						element={<Forgotpassword />}
					/>
				</Route>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_DEFINITION_PLUGIN}
					element={<DefinationPlugin />}
				/>
				<Route
					path={ROUTE_PATHS.RICH_TEXT_BOX_POPUP}
					element={<RichTextBoxPopup />}
				/>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_REPORT_DEFINITION_PLUGIN}
					element={<ReportDefinationPlugin />}
				/>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_DOCUMENT_LINK_PLUGIN}
					element={<DocumentLinkPlugin />}
				/>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_PAGE_DOCUMENT_LINK_PLUGIN}
					element={<PageDocumentLinkPlugin />}
				/>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_DOCUMENT_DEFINITION_ALERT_PLUGIN}
					element={<DocumentDefinationAlertPlugin />}
				/>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_ALERT_DOCUMENT_LINK_PLUGIN}
					element={<AlertDocumentLinkPlugin />}
				/>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_DOCUMENT_PDF_PAGE_DOWNLOAD}
					element={<PDFPageDownload />}
				/>
				<Route
					path={ROUTE_PATHS.ALERT_DOC_DOWNLOAD}
					element={<AlertDocumentDownload />}
				/>
				<Route
					path={ROUTE_PATHS.CONDITION_OF_USE_DOC_DOWNLOAD}
					element={<ConditionOfUseDocumentDownload />}
				/>
				<Route
					path={ROUTE_PATHS.PRIVATE_BANKING_DOC_DOWNLOAD}
					element={<PrivateBankingDocumentDownload />}
				/>
				<Route
					path={ROUTE_PATHS.DOWNLOAD_RESOURCE}
					element={<DownloadResource />}
				/>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_GRID_DEFINITION_PLUGIN}
					element={<GridDefinationPlugin />}
				/>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_GRID_DOCUMENT_LINK_PLUGIN}
					element={<GridDocumentLinkPlugin />}
				/>
				<Route
					path={ROUTE_PATHS.SEARCH_DOC_DOWNLOAD}
					element={<SearchDocumentDownload />}
				/>
				<Route
					path={ROUTE_PATHS.SOURCE_DOC_DOWNLOAD}
					element={<SourceDocumentDownload />}
				/>
				<Route
					path={ROUTE_PATHS.VIEW_DOCUMENT}
					element={<SourceDocumentDownload />}
				/>
				<Route
					path={ROUTE_PATHS.CK_EDITOR_VIEW_DOCUMENT}
					element={<ViewDocument />}
				/>
				<Route path={ROUTE_PATHS.PDF_VIEWER} element={<PDFViewerPage />} />
				<Route
					path={ROUTE_PATHS.CK_EDITOR_IMAGE_BROWSER}
					element={<CkEditorImageBrowser />}
				/>
				<Route path={ROUTE_PATHS.PAGE_MOVED} element={<PageMoved />} />
				<Route path={ROUTE_PATHS.HANDLE_CFM_LINKS} element={<HandelCFMLinks />} />
				{/* Intermediate Auth - for pages between login+OTP and full authentication */}
				<Route element={<IntermediateAuthRoute />}>
					<Route
						path={ROUTE_PATHS.TERMS_AND_CONDITIONS}
						element={<TermsAndConditions />}
					/>
				</Route>
				<Route
					path={ROUTE_PATHS.PUBLIC_STANDARD_ERROR}
					element={<PublicStandardError />}
				/>
				<Route
					path={ROUTE_PATHS.PUBLIC_ACCESS_ERROR}
					element={<PublicAccessError />}
				/>
				<Route path={ROUTE_PATHS.ERROR_HANDLING} element={<TestErrorHandling />} />
				<Route
					path={ROUTE_PATHS.STANDARD_ERROR}
					element={<StandardError />}
				/>
				<Route path={ROUTE_PATHS.DOWNLOAD_SUCCESS} element={<DownloadSuccess />} />
				<Route
					path={ROUTE_PATHS.ACCESS_ERROR}
					element={<AccessError />}
				/>
				{/* Private */}
				<Route element={<PrivateRoute />}>
					<Route path={ROUTE_PATHS.HOME} element={<CategoryHomePage />} />
					<Route
						path={ROUTE_PATHS.CATEGORY_HOME}
						element={<CategoryHomePage />}
					/>
					<Route path={ROUTE_PATHS.SUPPORT} element={<div>Support Page</div>} />
					{/* <Route path={ROUTE_PATHS.PRIVATE_BANKING} element={<PrivateBanking />} /> */}
					<Route
						path={ROUTE_PATHS.API_PRODUCT}
						element={withReportSkeleton(<PrivateBankingReport />, <PrivateBankingPageSkeleton />)}
					/>
					{/* <Route path={ROUTE_PATHS.PRIVATE_BANKING_REPORT} element={<PrivateBankingReport />} /> */}	

					<Route element={<LeiRoutesProvider />}>
						<Route path={ROUTE_PATHS.LEI_HOME_PAGE} element={<LEIHomePage />} />
						<Route path={ROUTE_PATHS.LEI_OUT_COME_PAGE} element={<LEIOutComePage />} />
						<Route path={ROUTE_PATHS.LEI_NETTING_ANALYSIS_PAGE} element={<LEINettingAnalysisPage />} />
					</Route>
					
					<Route
						path={ROUTE_PATHS.CHANGE_PASSWORD}
						element={<ChangePassword />}
					/>
					<Route path={ROUTE_PATHS.ALL_ALERTS} element={<AllAlerts />} />
					<Route path={ROUTE_PATHS.ADD_ALERT} element={<AddAlert />} />
					<Route path={ROUTE_PATHS.FIND_GRIDS} element={<FindGrid />} />
					<Route path={ROUTE_PATHS.VIEW_GRID} element={withReportSkeleton(<ViewGrid />, <GridPageSkeleton />)} />
					<Route path={ROUTE_PATHS.CONTENT_TILE_VIEW_GRID} element={<ContentTileViewGridPage />} />
					<Route path={ROUTE_PATHS.EDIT_GRID} element={withReportSkeleton(<EditGrid />, <GridPageSkeleton />)} />
					<Route path={ROUTE_PATHS.GRID_SETTINGS} element={<GridSettings />} />
					<Route
						path={ROUTE_PATHS.VIEW_GRID_FILTER}
						element={withReportSkeleton(<GridViewFilter />, <GridPageSkeleton />)}
					/>
					<Route path={ROUTE_PATHS.DRAFT_GRIDS} element={<FindGrid />} />
					<Route path={ROUTE_PATHS.ARCHIVED_GRIDS} element={<FindGrid />} />
					<Route path={ROUTE_PATHS.FIND_PAGES} element={<FindPage />} />
					<Route path={ROUTE_PATHS.DRAFT_PAGES} element={<FindPage />} />
					<Route path={ROUTE_PATHS.ARCHIVED_PAGES} element={<FindPage />} />
					<Route path={ROUTE_PATHS.FIND_REPORTS} element={<FindReport />} />
					<Route path={ROUTE_PATHS.GUEST_USER_REPORTS} element={<GuestUserReports />} />
					<Route path={ROUTE_PATHS.OPINION_DRAFT} element={<FindReport />} />
					<Route path={ROUTE_PATHS.OPINION_ARCHIVED} element={<FindReport />} />
					<Route path={ROUTE_PATHS.GUEST_USER_REPORTS} element={<GuestUserReports />} />
					<Route path={ROUTE_PATHS.ADD_NEW_REPORT} element={<AddNewReport />} />
					<Route path={ROUTE_PATHS.REPORT_VIEW} element={<ReportView />} />
					<Route path={ROUTE_PATHS.REPORTING} element={<Reporting />} />
					<Route path={ROUTE_PATHS.MY_REVIEWS} element={<MyReviews />} />
					<Route
						path={ROUTE_PATHS.BOOKMARKED_REPORT}
						element={<FindReport />}
					/>
					<Route path={ROUTE_PATHS.RECENT_VIEWED} element={<FindReport />} />
					<Route path={ROUTE_PATHS.RECENT_UPDATES} element={<FindReport />} />
					<Route path={ROUTE_PATHS.PREVIEW_PAGES} element={withReportSkeleton(<SpeedPage />, <SpeedPageFallback />)} />
					<Route path={ROUTE_PATHS.PRODUCT} element={<Product />} />
					<Route
						path={ROUTE_PATHS.PRODUCT_REDIRECT}
						element={<ProductRedirect />}
					/>
					<Route
						path={ROUTE_PATHS.ADVANCED_SEARCH}
						element={<AdvancedSearch />}
					/>
					<Route path={ROUTE_PATHS.MY_ALERT} element={<MyAlert />} />
					<Route
						path={ROUTE_PATHS.REPORT_SINGLE}
						element={withReportSkeleton(<ReportSingleContainer />)}
					/>
					<Route
						path={ROUTE_PATHS.REPORT_TAB}
						element={withReportSkeleton(<ReportTabContainer />)}
					/>
					<Route
						path={ROUTE_PATHS.REPORT_MULTITEMPLATE}
						element={withReportSkeleton(<ReportMultiContainer />)}
					/>
					{/* <Route
            path={ROUTE_PATHS.REPORT_VIEW_COMPARE}
            element={<ReportViewCompareContainer />}
          /> */}
					<Route
						path={ROUTE_PATHS.REPORT_VIEW_COMPARE}
						element={<ReportCompare />}
					/>
					<Route
						path={ROUTE_PATHS.OPINION_ADD}
						element={<EditReportContainer />}
					/>
					<Route
						path={ROUTE_PATHS.OPINION_COMPARE}
						element={withReportSkeleton(<OpinionCompare />, <ComparePageSkeleton includePageHeader />)}
					/>
					<Route
						path={ROUTE_PATHS.OPINION_COMPARE_TAB}
						element={withReportSkeleton(<OpinionCompareTab />, <ComparePageSkeleton includePageHeader />)}
					/>
					<Route path={ROUTE_PATHS.TOAST_DEMO} element={<ToastDemo />} />
					<Route
						path={ROUTE_PATHS.EDITORIAL_PAGE}
						element={withReportSkeleton(<EditorialPage />, <EditorialPageSkeleton />)}
					/>
					<Route path={ROUTE_PATHS.EDIT_PAGE} element={<EditPage />} />
					<Route
						path={ROUTE_PATHS.VERSION_HISTORY}
						element={withReportSkeleton(<ReportVersionHistory />, <VersionHistorySkeleton />)}
					/>
					<Route
						path={ROUTE_PATHS.PAGE_VERSION_HISTORY}
						element={<PageVersionHistory />}
					/>
					<Route
						path={ROUTE_PATHS.REPORT_ONLY_CHANGES}
						element={<ReportChangeOnly />}
					/>
					<Route
						path={ROUTE_PATHS.REPORT_VERSION_COMPARISON}
						element={<ReportChangeOnly />}
					/>
					<Route
						path={ROUTE_PATHS.GRID_VERSION_HISTORY}
						element={<GridVersionHistory />}
					/>
					<Route path={ROUTE_PATHS.CK_EDITOR} element={<CKEditor />} />

					<Route
						path={ROUTE_PATHS.ADD_INTERNAL_NOTE}
						element={<AddInternalNote />}
					/>
					<Route
						path={ROUTE_PATHS.PAGE_VERSION_COMPARISION}
						element={<PageVersionComparison />}
					/>
					<Route
						path={ROUTE_PATHS.PAGE_VIEW_LINK}
						element={withReportSkeleton(<PageViewLink />, <EditorialPageSkeleton />)}
					/>
					<Route
						path={ROUTE_PATHS.REPORT_VIEW_LINK}
						element={withReportSkeleton(<ReportViewLink />, <ReportViewLinkFallback />)}
					/>
					<Route
						path={ROUTE_PATHS.GRID_VIEW_LINK}
						element={withReportSkeleton(<GridViewLink />, <GridPageSkeleton />)}
					/>
          <Route
            path={ROUTE_PATHS.GRID_VIEW}
            element={<GridView />}
          />

					<Route
						path={ROUTE_PATHS.INTERNAL_NOTES_ATTACHMENT}
						element={<InternalNotesAttachment />}
					/>
				</Route>

				{/* 404 — splat matches unknown URLs; legacy .cfm redirects here without mounting NotFound first */}
				<Route path={ROUTE_PATHS.NOT_FOUND} element={<UnknownPathRoute />} />
				<Route path={ROUTE_PATHS.LOGOUT} element={<Logout />} />
			</Routes>
				<RouteSuspenseTracker />
			</Suspense>
			<V2ProgressReporter />
		</PageProgressProvider>
	);
};

const AppRoutes = () => {
	return (
		<BrowserRouter>
			<AppLocationGuard />
			<AppRoutesContent />
		</BrowserRouter>
	);
};

export default AppRoutes;
